/* eslint-disable no-unused-vars */
import { unwrapResult } from '@reduxjs/toolkit';
import { useNavigate } from 'react-router';
import { slices, useAppDispatch, useAppSelector } from 'src/redux';
import { useSnackBar } from '../use-snackbar';
import { AuditListPayload } from 'src/types';
import { formatAuditListParams } from 'src/utils/audits';
import { useState } from 'react';

const { selectors: auditSelectors, actions: auditActions } = slices.audit;

export const useAudit = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const snackBar = useSnackBar();

  const isLoading = useAppSelector(auditSelectors.selectIsLoading);
  const auditList = useAppSelector(auditSelectors.selectAuditList);
  const auditListParams = useAppSelector(auditSelectors.selectAuditParams);
  const paginationMeta = useAppSelector(auditSelectors.selectAuditPagination);

  const [auditableOptions, setAuditableOptions] = useState<string[]>([]);

  const fetchAuditList = async (params: AuditListPayload) => {
    try {
      await dispatch(auditActions.getAuditsThunkV2(params));

      if (params) {
        const urlParams = formatAuditListParams(params);
        navigate(`/app/audits?${urlParams.toString()}`);
      }
    } catch (error) {
      console.error('Error fetching audit list:', error);
    }
  };

  const setAuditListParams = async (params: AuditListPayload) => {
    try {
      dispatch(auditActions.auditFilterParams(params));
    } catch (error) {
      console.error('Error setting audit list params:', error);
    }
  };

  const getAuditableTypes = async () => {
    try {
      const response = unwrapResult(
        await dispatch(auditActions.getAuditableTypesThunk())
      );

      if (!response.success) {
        snackBar?.show({
          severity: 'error',
          message: response?.message || 'Failed to create template '
        });
      }

      setAuditableOptions(
        response?.originalData?.data?.map(
          (type) => type.auditable_type || ''
        ) || []
      );
    } catch (error) {
      console.error('Error fetching auditable types:', error);
    }
  };

  return {
    isLoading,
    auditList,
    auditListParams,
    paginationMeta,
    auditableOptions,
    fetchAuditList,
    setAuditListParams,
    getAuditableTypes
  };
};
